/**
 * @fileoverview Delegated link-click analytics (client island).
 * @description One delegated listener on a server-rendered container replaces
 * per-link `onClick` handlers, so the link markup itself stays server
 * component HTML. Every anchor click emits the site-intelligence
 * `site_action` event (feeding journey/engagement rollups) plus a
 * surface-specific GA4 event. Two consumers share the generic island:
 *
 * - `FooterAnalytics` — the global footer (`#footer`, surface=footer,
 *   GA4 `footer_link_click`, the event the pre-experiment footer always
 *   carried). Both events fire regardless of any experiment, so footer
 *   engagement stays measurable.
 * - `ResourceDoormat` — the section-contextual doormat band
 *   (`#resource-doormat`, surface=resource_doormat, GA4
 *   `doormat_link_click`). The demoted deep links moved off `#footer` in the
 *   footer redesign; without their own delegate those ~8 destinations would
 *   go dark in engagement metrics (`r3545906344`, `r3545906352`). The
 *   surface is deliberately NOT `footer` so post-redesign footer CTR
 *   comparisons stay honest.
 */

"use client"

import { useEffect } from "react"

import { sendEvent, trackSiteAction } from "~/lib/gtag"
import {
  normalizeActionLabel,
  type SiteActionSurface,
} from "~/lib/site-intelligence/actions"

/** DOM id of the footer element (see FooterShell). */
const FOOTER_ELEMENT_ID = "footer"

/**
 * Accessible-ish label for an anchor: aria-label when present, else visible
 * text with aria-hidden decorations (arrow glyphs) stripped, else the href.
 *
 * @param {HTMLAnchorElement} anchor - Clicked anchor.
 * @returns {string} Label for the analytics events.
 */
function anchorLabel(anchor: HTMLAnchorElement): string {
  const ariaLabel = anchor.getAttribute("aria-label")?.trim()
  if (ariaLabel) {
    return ariaLabel
  }
  const clone = anchor.cloneNode(true) as HTMLAnchorElement
  for (const decoration of clone.querySelectorAll("[aria-hidden]")) {
    decoration.remove()
  }
  const text = clone.textContent?.trim()
  return text || (anchor.getAttribute("href") ?? "")
}

/**
 * Emits both analytics events for one anchor click.
 *
 * @param {HTMLAnchorElement} anchor - Clicked anchor.
 * @param {SiteActionSurface} surface - Surface for the `site_action` event.
 * @param {string} gaAction - GA4 event action name.
 * @returns {void}
 */
function emitLinkClick(
  anchor: HTMLAnchorElement,
  surface: SiteActionSurface,
  gaAction: string,
): void {
  const href = anchor.getAttribute("href") ?? ""
  const external = anchor.host !== window.location.host
  const label = anchorLabel(anchor)

  trackSiteAction({
    kind: external ? "outbound" : "navigation",
    label: external ? "social" : normalizeActionLabel(label),
    surface,
    element_text: label,
    destination: href,
  })
  sendEvent({
    action: gaAction,
    label,
    element_id: label,
  })
}

/**
 * Attaches a delegated click listener to the element with `targetId` and
 * instruments every anchor click inside it. Renders nothing.
 *
 * @param {Object} props - Component props.
 * @param {string} props.targetId - DOM id of the delegation root.
 * @param {SiteActionSurface} props.surface - Surface for `site_action`.
 * @param {string} props.gaAction - GA4 event action name.
 * @returns {null} No UI.
 */
export function DelegatedLinkAnalytics({
  targetId,
  surface,
  gaAction,
}: {
  targetId: string
  surface: SiteActionSurface
  gaAction: string
}): null {
  useEffect(() => {
    const root = document.getElementById(targetId)
    if (!root) {
      return
    }
    const onClick = (event: MouseEvent): void => {
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }
      const anchor = target.closest("a[href]")
      if (!(anchor instanceof HTMLAnchorElement) || !root.contains(anchor)) {
        return
      }
      emitLinkClick(anchor, surface, gaAction)
    }
    root.addEventListener("click", onClick)
    return () => root.removeEventListener("click", onClick)
  }, [targetId, surface, gaAction])

  return null
}

/**
 * Delegated click instrumentation for the global footer. Renders nothing.
 *
 * @returns {JSX.Element} The delegated listener island (no UI).
 */
export function FooterAnalytics(): JSX.Element {
  return (
    <DelegatedLinkAnalytics
      targetId={FOOTER_ELEMENT_ID}
      surface="footer"
      gaAction="footer_link_click"
    />
  )
}
